<template>
  <!-- {{config}} -->
  <div class="forecast-widget" :style="containerStyle">
    <div
      v-if="loading"
      class="d-flex justify-content-center align-items-center"
      :style="{ color: props.config.fontColor }"
    >
      <span>Loading forecast data...</span>
    </div>
    <template v-else>
      <div
        v-for="(day, idx) in forecastDays"
        :key="idx"
        class="forecast-row"
        :style="rowStyle"
      >
        <img
          :src="getIcon(day.day.condition.code)"
          :style="iconStyle"
          class="forecast-icon"
        />
        <span :style="tempStyle" class="forecast-temp">
          {{ Math.round(day.day.avgtemp_c) }}°
        </span>
        <span :style="dateStyle" class="forecast-date">
          {{ formatDate(day.date) }}
        </span>
      </div>
    </template>
  </div>
</template>

<script lang="ts">
export default {
  name: "ForecastWdgt",
};
</script>
<script setup lang="ts">
import { displayService } from "@/_services";
import { computed, defineProps, onMounted, ref } from "vue";
import moment from "moment";
import "moment/locale/fr";

const props = defineProps<{
  mainApp: boolean;
  config: any;
}>();

const loading = ref(false);
const weatherData: any = ref(null);
const fetchWeatherData = async () => {
  try {
    loading.value = true;
    const res = await displayService.getWeatherData(
      props.config.city,
      "fr",
      3,
      "no"
    );
    weatherData.value = res.data;
    loading.value = false;
  } catch (err) {
    console.error("Error fetching weather data:", err);
    loading.value = false;
  }
};

const forecastDays = computed(() => {
  return weatherData.value?.forecast?.forecastday || [];
});

const rowCount = computed(() => forecastDays.value.length || 3);
const rowHeight = computed(() => `${props.config.height / rowCount.value}px`);
const iconSize = computed(
  () => `${Math.min(props.config.width, props.config.height) * 0.18}px`
);
const tempFontSize = computed(
  () => `${Math.min(props.config.width, props.config.height) * 0.12}px`
);
const dateFontSize = computed(
  () => `${Math.min(props.config.width, props.config.height) * 0.07}px`
);

const containerStyle = computed(() => ({
  width: `${props.config.width}px`,
  height: `${props.config.height}px`,
  background: backgroundStyle.value,
  borderRadius: `${props.config.borderRadius}px`,
  display: "flex !important",
  flexDirection: "column",
  justifyContent: "space-between",
  overflow: "hidden",
}));

const rowStyle = computed(() => ({
  display: "flex !important",
  alignItems: "center",
  justifyContent: "space-between",
  height: rowHeight.value,
  width: "100%",
  padding: "0 10px",
  boxSizing: "border-box",
}));

const iconStyle = computed(() => ({
  width: iconSize.value,
  height: iconSize.value,
  flexShrink: 0,
}));

const tempStyle = computed(() => ({
  fontFamily: props.config.fontFamily,
  fontWeight: "bold",
  fontSize: tempFontSize.value,
  color: props.config.fontColor,
}));

const dateStyle = computed(() => ({
  fontFamily: props.config.fontFamily,
  fontSize: dateFontSize.value,
  color: props.config.fontColor,
}));

function formatDate(date: string) {
  return moment(date)
    .locale("fr")
    .format("ddd D MMM")
    .replace(/^([a-zA-Z])/, (match) => match.toUpperCase());
}

function getIcon(condition: number) {
  try {
    if (condition == 1000) {
      return new URL("@/assets/images/weather/sunny.png", import.meta.url).href;
    } else if (condition == 1006) {
      return new URL("@/assets/images/weather/cloudy.png", import.meta.url)
        .href;
    } else if (condition == 1009 || condition == 1030 || condition == 1147) {
      return new URL("@/assets/images/weather/overcast.png", import.meta.url)
        .href;
    } else if (condition == 1003) {
      return new URL(
        "@/assets/images/weather/partly_cloudy.png",
        import.meta.url
      ).href;
    } else if (
      condition == 1273 ||
      condition == 1252 ||
      condition == 1249 ||
      condition == 1246 ||
      condition == 1240 ||
      condition == 1201 ||
      condition == 1198 ||
      condition == 1195 ||
      condition == 1192 ||
      condition == 1189 ||
      condition == 1186 ||
      condition == 1183 ||
      condition == 1180 ||
      condition == 1153 ||
      condition == 1150 ||
      condition == 1063 ||
      condition == 1289
    ) {
      return new URL("@/assets/images/weather/rainy.png", import.meta.url).href;
    } else if (
      condition == 1264 ||
      condition == 1261 ||
      condition == 1258 ||
      condition == 1255 ||
      condition == 1243 ||
      condition == 1225 ||
      condition == 1222 ||
      condition == 1219 ||
      condition == 1216 ||
      condition == 1213 ||
      condition == 1210 ||
      condition == 1066 ||
      condition == 1114 ||
      condition == 1117
    ) {
      return new URL("@/assets/images/weather/snowy.png", import.meta.url).href;
    } else if (
      condition == 1237 ||
      condition == 1168 ||
      condition == 1069 ||
      condition == 1072 ||
      condition == 1279 ||
      condition == 1282
    ) {
      return new URL("@/assets/images/weather/ice.png", import.meta.url).href;
    } else if (
      condition == 1087 ||
      condition == 1276 ||
      condition == 1279 ||
      condition == 1282
    ) {
      return new URL(
        "@/assets/images/weather/thunder_storm.png",
        import.meta.url
      ).href;
    } else if (condition == 1204 || condition == 1207) {
      return new URL("@/assets/images/weather/windy.png", import.meta.url).href;
    } else {
      return "";
    }
  } catch (e) {
    return "";
  }
}

const getBackgroundColorWithOpacity = (bgColor: any) => {
  const opacityValue = props.config.opacity / 100;

  //bgcolor rgba/hsla=> return as it is
  if (bgColor.includes("rgba") || bgColor.includes("hsla")) {
    return bgColor;
  }

  //convert hex to rgba
  if (bgColor.startsWith("#")) {
    const hex = bgColor.replace("#", "");
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    return `rgba(${r}, ${g}, ${b}, ${opacityValue})`;
  }

  return bgColor;
};

function hexToRgba(hex: string, alpha: number) {
  let c = hex.replace("#", "");
  if (c.length === 3)
    c = c
      .split("")
      .map((x) => x + x)
      .join("");
  const num = parseInt(c, 16);
  const r = (num >> 16) & 255;
  const g = (num >> 8) & 255;
  const b = num & 255;
  return `rgba(${r},${g},${b},${alpha})`;
}

const backgroundStyle = computed(() => {
  // gradient
  if (props.config.bgConfig?.selectedOption == "gradient") {
    const gradient = props.config.bgConfig.gradient;
    const opacityValue = props.config.opacity / 100;

    // Apply global opacity to gradient colors
    const start = hexToRgba(
      gradient.startColor,
      (gradient.startColorOpacity || 1) * opacityValue
    );
    const end = hexToRgba(
      gradient.endColor,
      (gradient.endColorOpacity || 1) * opacityValue
    );

    //direction
    let linearDirection = "to right";
    let radialPosition = gradient.direction || "center";

    switch (gradient.direction) {
      case "left-right":
        linearDirection = "to right";
        break;
      case "right-left":
        linearDirection = "to left";
        break;
      case "top-bottom":
        linearDirection = "to bottom";
        break;
      case "bottom-top":
        linearDirection = "to top";
        break;
      case "top-left-bottom-right":
        linearDirection = "135deg";
        break;
      case "top-right-bottom-left":
        linearDirection = "225deg";
        break;
      case "bottom-left-top-right":
        linearDirection = "45deg";
        break;
      case "bottom-right-top-left":
        linearDirection = "315deg";
        break;
      default:
        linearDirection = "to right";
    }

    if (gradient.type === "linear") {
      return `linear-gradient(${linearDirection}, ${start}, ${end})`;
    } else {
      //radial
      return `radial-gradient(circle at ${radialPosition}, ${start}, ${end})`;
    }
  }

  //bgColor
  if (props.config.bgConfig?.selectedOption == "color") {
    return getBackgroundColorWithOpacity(props.config.bgConfig.fillColor);
  }

  //image
  if (props.config.bgConfig?.selectedOption == "image") {
    const imageConfig = props.config.bgConfig.image;

    if (imageConfig.src && imageConfig.src.length > 0) {
      const imageUrl = imageConfig.src[0].url;

      //bg props
      const backgroundSize = imageConfig.backgroundSize || "cover";
      const backgroundPosition = imageConfig.backgroundPosition || "center";
      const backgroundRepeat = imageConfig.backgroundRepeat || "no-repeat";

      let background = "";
      //overlay
      if (imageConfig.overlay && imageConfig.overlayColor) {
        const overlayOpacity = (imageConfig.overlayOpacity || 0) / 100;
        const overlayRgba = hexToRgba(imageConfig.overlayColor, overlayOpacity);
        background += `linear-gradient(${overlayRgba}, ${overlayRgba}), `;
      }
      background += `url(${imageUrl}) ${backgroundPosition} / ${backgroundSize} ${backgroundRepeat}`;
      return background;
    }

    return "transparent";
  }

  //og fill color
  return getBackgroundColorWithOpacity(props.config.backgroundColor);
});

onMounted(async () => {
  await fetchWeatherData();
});
</script>

<style scoped>
.forecast-widget {
  box-sizing: border-box;
}
</style>
